import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Check } from "lucide-react";
import { PaywallModal } from "@/components/PaywallModal";
import LandingNav from "@/components/LandingNav";
import SEOHead from "@/components/SEOHead";
import { motion, useReducedMotion } from "framer-motion";

const FREE_FEATURES = [
  "Up to 15 contacts",
  "Birthday reminders via email",
  "5 messages per month",
  "Messages in any language",
  "Daily relational wisdom",
  "Installable on any device",
  "Personalised gifting",
];

const PLUS_FEATURES = [
  { text: "Everything in Free", comingSoon: false },
  { text: "Unlimited contacts", comingSoon: false },
  { text: "All occasion types", comingSoon: false },
  { text: "Unlimited messages", comingSoon: false },
  { text: "WhatsApp reminders", comingSoon: false },
  { text: "Custom reminder lead time", comingSoon: false },
  { text: "Custom message tones", comingSoon: false },
  { text: "Relationship health nudges", comingSoon: false },
  { text: "Relationship timeline", comingSoon: true },
  { text: "Messages that learn your voice", comingSoon: true },
  { text: "Personalised gifting", comingSoon: false },
];

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("annual");
  const [paywallOpen, setPaywallOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "cancelled") {
      toast.error("Checkout cancelled", {
        description: "No payment was taken. You can upgrade any time.",
        duration: 5000,
      });
      window.history.replaceState({}, "", "/pricing");
    }
  }, []);

  const handlePlusCTA = () => {
    if (!user) {
      localStorage.setItem("upgrade_intent", "plus");
      navigate("/signup");
    } else {
      setPaywallOpen(true);
    }
  };

  const fadeUp = prefersReducedMotion
    ? {}
    : { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

  const staggerContainer = prefersReducedMotion
    ? {}
    : { initial: "hidden", whileInView: "visible", viewport: { once: true } };

  const staggerChildren = (stagger = 0.1) =>
    prefersReducedMotion
      ? {}
      : { variants: { hidden: {}, visible: { transition: { staggerChildren: stagger } } } };

  const staggerItem = prefersReducedMotion
    ? {}
    : {
        variants: {
          hidden: { opacity: 0, y: 20 },
          visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } },
        },
      };

  const hoverScale = prefersReducedMotion ? {} : { whileHover: { scale: 1.02 } };

  return (
    <div className="flex flex-col bg-background min-h-screen">
      <SEOHead
        title="Dearestly — Pricing"
        description="Dearestly Free is free forever. Dearestly Plus is USD 4.99/month or USD 39.99/year — unlimited contacts, all occasion types, unlimited messages, and more."
        path="/pricing"
      />
      <LandingNav />

      <section className="bg-secondary px-6 py-20 md:py-28 flex-1">
        <div className="mx-auto max-w-4xl text-center">
          <motion.span
            className="mb-4 inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent"
            {...fadeUp}
            transition={{ duration: 0.5 }}
          >
            Pricing
          </motion.span>
          <motion.h2
            className="mb-3 font-serif text-3xl font-semibold text-foreground md:text-4xl"
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Start free. Upgrade when Dearestly becomes indispensable.
          </motion.h2>
          <motion.p
            className="mb-4 text-muted-foreground"
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            Less than one coffee a month. No contracts. Cancel any time.
          </motion.p>
          <motion.p
            className="mb-12 font-serif text-lg italic text-muted-foreground"
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Most people upgrade when they realise Dearestly knows their relationships better than they do.
          </motion.p>

          {/* Billing toggle */}
          <motion.div
            className="mb-10 flex items-center justify-center gap-1"
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.25 }}
          >
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                billingCycle === "monthly"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                billingCycle === "annual"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Annual
            </button>
          </motion.div>

          {/* Cards */}
          <motion.div
            className="mx-auto grid max-w-3xl gap-6 md:grid-cols-2"
            {...staggerContainer}
            {...staggerChildren(0.15)}
          >
            {/* Free card */}
            <motion.div
              className="flex flex-col rounded-2xl border border-border bg-card p-8 text-left shadow-sm transition-shadow hover:shadow-md"
              {...staggerItem}
              {...hoverScale}
            >
              <h3 className="font-serif text-xl font-semibold text-foreground">Dearestly Free</h3>
              <p className="mt-1 text-sm text-muted-foreground">Free forever</p>
              <ul className="mt-6 flex-1 space-y-3">
                {FREE_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                asChild
                variant="outline"
                className="mt-8 w-full rounded-full border-[1.5px] hover:bg-accent/10"
                style={{ borderColor: "#C9A84C", color: "#C9A84C" }}
                size="lg"
              >
                <Link to="/signup">Get started free →</Link>
              </Button>
            </motion.div>

            {/* Plus card */}
            <motion.div
              className="relative flex flex-col rounded-2xl border-2 border-accent bg-card p-8 text-left shadow-md transition-shadow hover:shadow-lg"
              {...staggerItem}
              {...hoverScale}
            >
              <span className="absolute -top-3 right-6 inline-block rounded-full bg-accent px-3 py-1 text-xs font-bold text-accent-foreground">
                Most popular
              </span>

              <div className="flex items-center gap-3">
                <h3 className="font-serif text-xl font-semibold text-foreground">Dearestly Plus</h3>
                {billingCycle === "annual" && (
                  <span className="rounded-full bg-accent px-2.5 py-0.5 text-[10px] font-bold text-accent-foreground">
                    SAVE 4 MONTHS
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {billingCycle === "annual" ? "USD 39.99 / year" : "USD 4.99 / month"}
              </p>

              <ul className="mt-6 flex-1 space-y-3">
                {PLUS_FEATURES.map((f) => (
                  <li key={f.text} className="flex items-start gap-2 text-sm text-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span>
                      {f.text}
                      {f.comingSoon && (
                        <span className="ml-1.5 text-xs text-muted-foreground italic">
                          — coming soon
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <Button
                className="mt-8 w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
                size="lg"
                onClick={handlePlusCTA}
              >
                Start Dearestly Plus →
              </Button>
            </motion.div>
          </motion.div>

          <motion.p
            className="mt-10 text-sm text-muted-foreground"
            {...fadeUp}
            transition={{ duration: 0.5 }}
          >
            Dearestly Family and Dearestly Teams available for families and corporate teams.{" "}
            <a
              href="mailto:dearestly001@gmail.com"
              className="text-accent underline hover:text-accent/80"
            >
              Contact us to learn more →
            </a>
          </motion.p>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 bg-primary">
        <div className="mx-auto max-w-xl text-center">
          <p className="mb-1 font-serif text-xl tracking-widest text-primary-foreground">Dearestly.</p>
          <p className="mb-4 font-serif text-sm text-primary-foreground/60">Show up for the people you love.</p>
          <div className="mb-4 flex justify-center gap-6 text-sm text-primary-foreground/60">
            <Link to="/privacy" className="hover:text-primary-foreground transition-colors">Privacy Notice</Link>
            <Link to="/terms" className="hover:text-primary-foreground transition-colors">Terms of Use</Link>
            <Link to="/blog" className="hover:text-primary-foreground transition-colors">Blog</Link>
            <a href="mailto:dearestly001@gmail.com" className="hover:text-primary-foreground transition-colors">Contact Us</a>
          </div>
          <p className="text-xs text-primary-foreground/60">© 2026 Kena Technologies Limited. All rights reserved.</p>
        </div>
      </footer>

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        title="Upgrade to Dearestly Plus"
        benefit="Unlimited contacts, all occasion types, unlimited messages, and more."
      />
    </div>
  );
};

export default Pricing;
